(ns io.daskul.webapp.widgets.dashboard.program-table
  "Programs widget (#729 ④) — top programs by revenue, each row carrying its own
   new-enrollment sparkline, so 'which programs are growing / fading' reads at a
   glance without a spaghetti multi-line chart.

   Expects {:widget :program-table :label :description
            :rows [{:name :enrollments :revenue :spark [counts…]} …]}."
  (:require
   [clojure.string :as str]
   [uix.core :refer [defui $]]
   [io.daskul.webapp.ui :as ui]
   [io.daskul.webapp.ui.icons :as icons]
   [io.daskul.webapp.widgets.dashboard.trend-logic :as tl]))

(defn- php [v]
  (.toLocaleString (js/Number. v) "en-PH"
    #js {:style "currency" :currency "PHP" :minimumFractionDigits 0 :maximumFractionDigits 0}))

;; The Δ arrow and the sparkline stroke both read the same tone (#945), so a
;; program whose last month dips but whose fitted trend is up shows a teal line
;; AND a teal ▲ — never the old last-vs-first mismatch.
(def ^:private tone->mantine {:up "teal" :down "red" :flat "gray" :none "dimmed"})
(def ^:private tone->stroke  {:up   "var(--mantine-color-teal-6)"
                              :down "var(--mantine-color-red-6)"
                              :flat "var(--mantine-color-gray-5)"})
(def ^:private tone->icon    {:up   icons/icon-trending-up
                              :down icons/icon-trending-down
                              :flat icons/icon-minus})

(defui sparkline
  "A tiny 12-month trend line; stroke colour follows the Δ tone (#945). Renders —
   when there's no trend to draw (tone :none — fewer than 3 fitted months)."
  [{:keys [values tone]}]
  (let [vs (vec values) n (count vs)]
    (if (or (= tone :none) (< n 2))
      ($ ui/text {:size "xs" :c "dimmed"} "—")
      (let [mx    (max 1 (reduce max 0 vs))
            w 116 h 20
            step  (/ w (dec n))
            pts   (str/join " " (map-indexed
                                  (fn [i v] (str (* i step) ","
                                                 (- h (* (/ v mx) (- h 3)) 1)))
                                  vs))]
        ($ :svg {:viewBox (str "0 0 " w " " h) :preserveAspectRatio "none"
                 :style #js {:width "116px" :height "20px" :display "inline-block"}}
          ($ :polyline {:points pts :fill "none"
                        :stroke (tone->stroke tone "var(--mantine-color-gray-5)")
                        :strokeWidth 1.8}))))))

(defui col-th
  "A header cell with an optional (i) tooltip explaining what the column measures."
  [{:keys [label help align]}]
  ($ ui/th {:style (when align {:textAlign align})}
    (if help
      ($ ui/group {:gap 4 :align "center" :wrap "nowrap" :component "span"
                   :justify (if (= align "right") "flex-end" "flex-start")
                   :style #js {:display "inline-flex"}}
        ($ :span label)
        ($ ui/tooltip {:label help :multiline true :w 240 :position "top" :withArrow true}
          ($ ui/text {:c "dimmed" :span true :style #js {:cursor "help" :display "inline-flex"} :lh 1}
            ($ icons/icon-info-circle {:size 12 :stroke 1.5}))))
      label)))

(defui program-table [{:keys [label description rows column-help]}]
  ($ ui/paper {:p "lg" :radius "md" :withBorder true}
    ($ ui/group {:gap 6 :align "center" :mb "md" :wrap "nowrap"}
      ($ ui/text {:size "sm" :fw 700} label)
      (when description
        ($ ui/tooltip {:label description :multiline true :w 260
                       :position "top-end" :withArrow true}
          ($ ui/text {:c "dimmed" :style #js {:cursor "help"} :lh 1}
            ($ icons/icon-info-circle {:size 14 :stroke 1.5})))))
    (if (empty? rows)
      ($ ui/text {:size "sm" :c "dimmed"} "No program revenue in this period.")
      ($ ui/table {:highlightOnHover true}
        ($ ui/thead
          ($ ui/tr
            ($ col-th {:label "Program"})
            ($ col-th {:label "Enrollments" :align "right" :help (:enrollments column-help)})
            ($ col-th {:label "Revenue"     :align "right" :help (:revenue column-help)})
            ($ col-th {:label "12-mo Trend" :align "right" :help (:trend column-help)})
            ($ col-th {:label "Δ"           :align "right" :help (:delta column-help)})))
        ($ ui/tbody
          (for [[i r] (map-indexed vector rows)]
            (let [d    (:delta r)                 ; fitted trend Δ, computed API-side (#945)
                  tone (tl/trend-tone d)]
              ($ ui/tr {:key i}
                ($ ui/td (:name r))
                ($ ui/td {:style {:textAlign "right" :fontFamily "Sora, sans-serif" :fontVariantNumeric "tabular-nums"}}
                  (str (:enrollments r)))
                ($ ui/td {:style {:textAlign "right" :fontFamily "Sora, sans-serif" :fontVariantNumeric "tabular-nums"}}
                  (php (:revenue r)))
                ($ ui/td {:style {:textAlign "right"}} ($ sparkline {:values (:spark r) :tone tone}))
                ($ ui/td {:style {:textAlign "right"}}
                  (if (or (nil? d) (zero? d))
                    ($ ui/text {:size "sm" :c "dimmed"} "—")
                    (let [color (tone->mantine tone)]
                      ($ ui/group {:gap 4 :justify "flex-end" :align "center" :wrap "nowrap"}
                        ($ ui/text {:c color :lh 1} ($ (tone->icon tone) {:size 14 :stroke 2}))
                        ($ ui/text {:size "sm" :fw 600 :c color
                                    :style {:fontFamily "Sora, sans-serif" :fontVariantNumeric "tabular-nums"}}
                          (str (when (pos? d) "+") d))))))))))))))
